import type { NextPage } from 'next'

import { CategoryView } from '@components/category'
import type { CustomFacetValue, ManufacturerFacet, ProductSearchResult } from '@model/product'
import * as R from 'ramda'
import { Helmet } from '@components/helmet'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isNotNil } from '@utils/simple-helper'
import type {
  FilterValue,
  GetBrandDataHandler,
  ICategoryFiltersContext,
  IsBrandFilterEnabledHandler,
  SetCategoryBannersHandler,
  SetCategoryDataHandler,
  SetSortingOption,
  ToggleBrandFilterHandler,
} from '@context/category-filters'
import { CategoryFiltersContext } from '@context/category-filters'
import type { ParsedUrlQuery } from 'querystring'
import type { SortOrder } from '@model/product/ProductSearchResult'
import type { CustomFacet } from '@model/product/CustomFacet'
import { getFacetTitle } from '@model/product/CustomFacet'
import { useTranslate } from '@hooks/useTranslation'
import type { ImagePageBanner } from '@api/content'
import { useAppState } from '@context/state'
import type { CategoryTip } from '@model/category/CategoryTip'
import { AppStateTypes } from '@context/reducer'
import { nicerLog } from '@utils/console-utils'
import { unSlug } from '@utils/slugs'
import { getCategoryDetails } from '@utils/product/getCategoryDetails'
import type { CatalogFromContent } from '@api/content'
import ModalStateProvider from '@context/ModalContext'
import { stringOfCustomFacets } from '@components/category/CategoryView/CategoryView'
import { sessionStorageHelper } from '@utils/localStorageHelper'
import { useInitialData } from '@context/initialDataContext'

const groupCatalogs = R.groupBy<CatalogFromContent>(R.prop('info'))

function routerQueryValueToArray(queryValue: ParsedUrlQuery[0]): string[] {
  if (!queryValue) {
    return []
  }

  if ((queryValue as string) && (queryValue as string)?.localeCompare) {
    return [queryValue as string]
  }

  return queryValue as string[]
}

function noEmptyArrayValues(obj: { [key: string]: any[] }): { [key: string]: any[] } {
  return Object.keys(obj)
    .map((key) => {
      if (obj[key]?.length > 0) {
        return key
      }

      return null
    })
    .filter((key) => !!key)
    .reduce((newObj, key) => ({ ...newObj, [key!]: obj[key!] }), {})
}

type NameAndDescription = Pick<CategoryTip, 'name' | 'description'>

const findCategoryNameAndDescription = (categories: CategoryTip[], categoryId: number): NameAndDescription => {
  const flatCategories: CategoryTip[] = []

  const extract = (categories: CategoryTip[]) => {
    categories?.forEach((cat: CategoryTip) => {
      flatCategories.push(cat)
      if (cat.children) {
        extract(cat.children)
      }
    })
  }
  extract(categories)

  if (!flatCategories.length) return { name: '', description: '' }
  try {
    return R.compose(
      R.pick(['name', 'description']),
      R.find<CategoryTip>(({ id }) => id === categoryId)
    )(flatCategories) as NameAndDescription
  } catch (e) {
    return { name: '', description: '' }
  }
}

interface CategoryPageProps {
  catalogs?: CatalogFromContent[]
  manufacturerId?: number
  manufacturerIds?: number[]
  manufacturerName?: string
  jsxData?: string
}

export const CategoryPage: NextPage<CategoryPageProps> = ({
  catalogs = [],
  manufacturerId,
  manufacturerIds,
  manufacturerName,
  jsxData,
}) => {
  const { events } = useRouter()
  const catalogGroups = groupCatalogs(catalogs)

  const router = useRouter()

  const {
    initialData: { categories },
  } = useInitialData()

  const { slug, term, name, catalogId, ...restParams } = router.query
  const previousCategory = useRef<string | string[] | undefined>()
  const id = typeof slug === 'string' ? unSlug(slug) : undefined
  useEffect(() => {
    if (previousCategory.current !== slug && previousCategory.current) {
      resetAll()
    }
    previousCategory.current = slug
  }, [slug])
  const searchTerm = routerQueryValueToArray(term)?.[0] || undefined

  const queryString = useMemo(() => {
    const usp = new URLSearchParams((restParams ?? {}) as any)
    usp.sort()
    return usp.toString() ?? ''
  }, [restParams])

  const [category, setCategory] = useState<ProductSearchResult | null>(null)
  const [sorting, setSorting] = useState<SortOrder>(null)
  const [banners, setBanners] = useState<ImagePageBanner[]>([])

  const { minPrice: minAvailablePrice, maxPrice: maxAvailablePrice } = category ?? {}

  const encodeBrandFilter = (brand: ManufacturerFacet | Pick<ManufacturerFacet, 'id' | 'name'>): FilterValue => {
    const { id, name } = brand
    const query = `${name}-${id}`
    return {
      id: id.toString(),
      name,
      query,
    }
  }
  const decodeBrandFilter = (queryValueString: string): FilterValue => {
    const [name, id] = queryValueString.split('-')
    return {
      id,
      name,
      query: queryValueString,
    }
  }

  const [brandsFilters, setBrandsFilters] = useState<FilterValue[]>(
    routerQueryValueToArray(router.query.brands).map((b) => decodeBrandFilter(b))
  )

  const routerQuery = router.query
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false)

  const isBrandFilterEnabled: IsBrandFilterEnabledHandler = (brand) => {
    const { id } = brand

    return !!brandsFilters.find((brand) => brand.id.toString() === id.toString())
  }

  const toggleBrandFilter: ToggleBrandFilterHandler = (brand) => {
    const { id, name } = brand

    const isEnabled = isBrandFilterEnabled(brand)

    if (isEnabled) {
      setBrandsFilters((prevBrandsFiltersState) =>
        prevBrandsFiltersState.filter((f) => f.id.toString() !== id.toString())
      )
    } else {
      setBrandsFilters((prevBrandsFiltersState) => [
        ...prevBrandsFiltersState,
        encodeBrandFilter({ id: parseInt(id.toString()), name }),
      ])
    }
  }

  const [customs, setCustoms] = useState<FilterValue[]>(
    []
    // (Object.keys(router.query ?? {}) ?? []).reduce((facets: FilterValue[], queryKey) => {
    //   const foundFacet =
    //   return [
    //     ...(facets ?? []),
    //     {
    //       id: query,
    //     } as FilterValue,
    //   ]
    // }, [] as FilterValue[])
    // routerQueryValueToArray(router.query).map((facet) => decodeCustomFilter(facet) ?? [])
  )

  const customFacets = category?.customFacets

  useEffect(() => {
    if (
      JSON.stringify(category?.customFacets) === JSON.stringify(sessionStorageHelper.get('customFacets')) ||
      !category?.customFacets
    )
      return

    sessionStorageHelper.set('customFacets', category?.customFacets)
  }, [category?.customFacets])

  const { translate } = useTranslate()
  const transformQueryName = (name: string) => {
    const replace = name.replace(/&&/g, '_')
    return replace.replace(/[^a-zA-Zа-яА-ЯёЁ0-9_]/g, '-')
    // const nameArr = name.split('.')
    // return nameArr.slice(1, nameArr.length).join('-')
  }
  const [minPrice, setMinPrice] = useState<number | null>(null)
  const [maxPrice, setMaxPrice] = useState<number | null>(null)
  const {
    dispatch,
    state: { activeCategoryPage },
  } = useAppState()

  useEffect(() => {
    const handle: (path: string) => void = (fullPath) => {
      const offset = window.pageYOffset || document.documentElement.scrollTop
      if (offset > 1 && fullPath.includes('product')) {
        nicerLog(`Leaving category page ${fullPath} scrolled to ${offset}`)
        dispatch({ type: AppStateTypes.setTopOffset, payload: offset })
      }
    }

    events.on('beforeHistoryChange', handle)

    return () => {
      events.off('beforeHistoryChange', handle)
    }
  }, [events])

  const queryURLParams = useMemo(() => {
    const customFacetsParamsObject = noEmptyArrayValues(
      (customs ?? []).reduce(
        (params, facet) => ({
          ...params,
          [facet.name]: [...(params[facet.name as keyof typeof params] ?? []), facet.query],
        }),
        {}
      ) ?? {}
    )
    const paramsObject = noEmptyArrayValues(brandsFilters.length ? { brands: brandsFilters.map((b) => b.query) } : {})

    const minPriceObject = minPrice ? { minPrice } : {}
    // const minPriceObject = minPrice && minPrice !== minAvailablePrice ? { minPrice } : {}
    // const minPriceObject = minPrice && minPrice !== minAvailablePrice ? { minPrice } : {}
    const maxPriceObject = maxPrice ? { maxPrice } : {}
    // const maxPriceObject = maxPrice && maxPrice !== maxAvailablePrice ? { maxPrice } : {}
    const searchTermObject = searchTerm && !routerQueryValueToArray(id)?.[0] ? { searchTerm } : {}

    const urlParams = new URLSearchParams({
      ...paramsObject,
      ...customFacetsParamsObject,
      ...(sorting && { sorting }),
      ...minPriceObject,
      ...maxPriceObject,
      ...searchTermObject,
    } as any)
    urlParams.sort()

    return urlParams
  }, [brandsFilters, sorting, stringOfCustomFacets(customs), minPrice, maxPrice, searchTerm, id, name])

  // }, [brandsFilters, sorting, customs, minPrice, maxPrice, minAvailablePrice, maxAvailablePrice, searchTerm, id])

  const queryParams = useMemo(() => {
    return queryURLParams.toString() ?? ''
  }, [queryURLParams])

  const [customFiltersInitialized, setCustomFiltersInitialized] = useState(false)

  useEffect(() => {
    if (!customFiltersInitialized) {
      return
    }

    const customFacetsParamsObject = noEmptyArrayValues(
      (customs ?? []).reduce(
        (params, facet) => ({
          ...params,
          [facet.name]: [...(params[facet.name as keyof typeof params] ?? []), facet.query],
        }),
        {}
      ) ?? {}
    )
    const paramsObject = noEmptyArrayValues({ brands: brandsFilters.map((b) => b.query) })

    // eslint-disable-next-line no-console
    console.log(
      'Replacing',
      JSON.stringify({
        // ...router.query,
        ...(slug && { slug: router.query.slug }),
        ...(term && { term: router.query.term }),
        ...(name && { name: router.query.name }),
        ...(catalogId && { catalogId: router.query.catalogId }),
        ...paramsObject,
        ...customFacetsParamsObject,
      })
    )

    router
      .replace(
        {
          query: {
            // ...router.query,
            ...(slug && { slug: router.query.slug }),
            ...(term && { term: router.query.term }),
            ...(name && { name: router.query.name }),
            ...(catalogId && { catalogId: router.query.catalogId }),
            ...paramsObject,
            ...customFacetsParamsObject,
          },
        },
        undefined,
        { scroll: false }
      )
      .catch(console.error)
  }, [
    stringOfCustomFacets(customs),
    brandsFilters,
    customFiltersInitialized,
    minPrice,
    maxPrice,
    id,
    catalogId,
    term,
    name,
    slug,
  ])

  useEffect(() => {
    const brands = Array.isArray(router.query.brands) ? router.query.brands : [router.query.brands]
    if (brandsFilters.map((brand) => brand.query).join('') !== brands.join('')) {
      setBrandsFilters(routerQueryValueToArray(router.query.brands).map((b) => decodeBrandFilter(b)))
    }
  }, [router.query])

  useEffect(() => {
    const customFacetsSave = (sessionStorageHelper.get('customFacets') as CustomFacet[]) ?? customFacets
    if (customFiltersInitialized || !customFacetsSave) {
      return
    }

    setCustomFiltersInitialized(true)
    setCustoms(
      Object.keys(routerQuery)
        .filter((key) => key !== 'brands' && key !== 'id')
        .reduce((facets: FilterValue[], queryKey: string) => {
          return [
            ...facets,
            ...(routerQueryValueToArray(routerQuery[queryKey])
              .map((query) => {
                const name = queryKey
                const foundFacet = customFacetsSave?.find((f) => name === getFacetTitle(f.number))
                const foundFacetValue = foundFacet?.values?.find((v) => transformQueryName(v.name) === query)

                if (!foundFacet || !foundFacetValue) {
                  return null
                }

                return {
                  id: foundFacetValue.name,
                  group: foundFacet.number.toString(),
                  query,
                  name,
                } as FilterValue
              })
              .filter((f) => !!f) as FilterValue[]),
          ]
        }, [] as FilterValue[])
    )
  }, [customFiltersInitialized, routerQuery, customFacets, translate])

  const manufacturerFacets = category?.manufacturerFacets

  const brandsData = useMemo(() => {
    if (!manufacturerFacets?.length) {
      return []
    }

    return brandsFilters
      .map((b) => manufacturerFacets.find((m) => m.id.toString() === b.id.toString()))
      .filter((b) => !!b) as ManufacturerFacet[]
  }, [brandsFilters, manufacturerFacets])

  const getBrandData = useCallback<GetBrandDataHandler>(
    (brand) => {
      return manufacturerFacets?.find((m) => m.id.toString() === brand.id.toString()) ?? null
    },
    [manufacturerFacets]
  )

  if (category) {
    category.categoryFacets = category.categoryFacets?.filter(isNotNil)
    category.customFacets = category.customFacets?.filter(isNotNil)
    category.manufacturerFacets = category.manufacturerFacets?.filter(isNotNil)
  }

  const setCategoryData = useCallback<SetCategoryDataHandler>(setCategory, [])
  const setCategoryBanners = useCallback<SetCategoryBannersHandler>(setBanners, [])
  const setSortOrder = useCallback<SetSortingOption>(setSorting, [])

  useEffect(() => {
    if (!category?.manufacturerFacets?.length) {
      return
    }

    setBrandsFilters((prevBrandsFiltersState) =>
      prevBrandsFiltersState.filter(
        (v) => !!category?.manufacturerFacets.find((m) => m.id.toString() === v.id.toString())
      )
    )
    setCustomFiltersInitialized(false)
  }, [category])

  const encodeCustomFilter = (facet: CustomFacet, value: CustomFacetValue): FilterValue => {
    const normalName = getFacetTitle(facet.number)
    const query = transformQueryName(value.name)

    return {
      id: value.name,
      name: normalName,
      group: facet.number.toString(),
      query,
    }
  }

  const decodeCustomFilter = (queryValueString: string): FilterValue => {
    const [name, id] = queryValueString.split('-')
    // const foundFacet = customFacets?.values.find()
    return {
      id,
      name,
      group: queryValueString,
      query: queryValueString,
    }
  }

  const isCustomFilterEnabled = (facet: FilterValue): boolean => {
    // let value: FilterValue
    // if (isFilterValue(facet)) {
    //   value = facet
    // } else {
    //   value = encodeCustomFilter(facet)
    // }
    const value = facet
    if (value.id.split('&&').length) {
      return !!customs.find((f) => {
        return value.id.split('&&').includes(f.id) && f.group === value.group
      })
    }
    return !!customs.find((f) => f.group === value.group && f.id === value.id)
  }
  //   function isFish(pet: Fish | Bird): pet is Fish {
  //     return (<Fish>pet).swim !== undefined;
  //  }

  // Both calls to 'swim' and 'fly' are now okay.
  // if (isFish(pet)) {
  //   pet.swim()
  // } else {
  //   pet.fly()
  // }

  const toggleCustom = (facet: FilterValue) => {
    const value = facet
    const isEnabled = isCustomFilterEnabled(value)

    const group = value.id.split('&&')

    if (group.length > 1) {
      let copyCustoms = [...customs]
      group.forEach((item) => {
        const query = transformQueryName(item)
        const val = {
          id: item,
          name: facet.name,
          group: facet.group,
          query: query,
        }

        const isEnabled = isCustomFilterEnabled(val)

        if (isEnabled) {
          copyCustoms = copyCustoms.filter(
            (f) => f.id.toString() !== val.id.toString() && f.group?.toString() !== val.id.toString()
          )
        } else {
          copyCustoms.push(val)
        }
      })
      setCustoms(copyCustoms)
    } else {
      if (isEnabled) {
        setCustoms(
          customs.filter((f) => f.id.toString() !== value.id.toString() && f.group?.toString() !== value.id.toString())
        )
      } else {
        setCustoms([...customs, value])
      }
    }
  }

  const resetAll = () => {
    setCustoms([])
    setBrandsFilters([])
    setMinPrice(null)
    setMaxPrice(null)
  }

  const filtersContextValue: ICategoryFiltersContext = {
    brands: brandsFilters,
    custom: {},
    brandsData,
    getBrandData,
    encodeBrandFilter,
    decodeBrandFilter,
    toggleBrandFilter,
    isBrandFilterEnabled,
    queryParams,
    setCategoryData,
    setCategoryBanners,
    setSortOrder,
    toggleCustom,
    encodeCustomFilter,
    decodeCustomFilter,
    isCustomFilterEnabled,
    customs,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    resetAll,
  }

  const { name: categoryName, description: categoryDescription } = id
    ? findCategoryNameAndDescription(categories, parseFloat(id as string))
    : { name: '', description: '' }
  const categoryDetails = categoryDescription ? getCategoryDetails(categoryDescription) : null

  const pageTitle = manufacturerName || categoryName

  return (
    <>
      {categoryDetails ? (
        <Helmet title={categoryDetails.title} description={categoryDetails.meta_description} type="category" />
      ) : (
        pageTitle && <Helmet title={pageTitle} type="category" />
      )}
      <CategoryFiltersContext.Provider value={filtersContextValue}>
        {categoryDetails?.h1 && <h1 style={{ display: 'none' }}>{categoryDetails.h1}</h1>}
        {categoryDetails?.h2 && <h2 style={{ display: 'none' }}>{categoryDetails.h2}</h2>}
        <ModalStateProvider>
          <CategoryView
            banners={banners}
            categoryData={category ?? undefined}
            currentPage={activeCategoryPage}
            categoryNameOrSearchTerm={categoryName ? categoryName : searchTerm}
            categoryName={categoryName}
            categoryDescription={categoryDetails?.description}
            catalogGroups={catalogGroups}
            manufacturerId={manufacturerId}
            manufacturerIds={manufacturerIds}
            manufacturerName={manufacturerName}
            jsxData={jsxData}
          />
        </ModalStateProvider>
      </CategoryFiltersContext.Provider>
    </>
  )
}

export default CategoryPage
